import { CST, Composer, type Document, Parser, isCollection, isPair, isScalar } from 'yaml'
import { type CatalogsConfig, parseCatalogsConfig } from '../types/CatalogConfig.ts'
import { type Options } from '../types/Options.ts'
import programError from './programError.ts'

type UpdateYamlCatalogDependenciesArgs = {
  fileContent: string
  upgrade: {
    path: string[] // e.g., ['catalogs', 'my-catalog', 'my-dep'] or ['catalog', 'my-dep']
    newValue: string // e.g., '^2.0.0'
  }
  options?: Options
  filePath?: string
}

/** Throws a user-facing error for invalid YAML syntax. */
function throwYamlSyntaxError(error: unknown, { options, filePath }: { options?: Options; filePath?: string }): never {
  const details = error instanceof Error ? error.message : String(error)
  const target = filePath ? ` in ${filePath}` : ''
  const message = `Invalid YAML syntax${target}. Unable to read catalog dependencies.\n${details}`

  if (options) {
    programError(options, message)
  }

  throw new Error(message)
}

/**
 * Change the scalar name and/or value of a collection item in a YAML document,
 * while keeping formatting consistent. Mutates the given document.
 *
 * Returns true when all requested updates were applied. Returns false when an
 * update could not be applied. The document may still be partially mutated when
 * false is returned (e.g. `newName` succeeds before `newValue` fails).
 */
function changeDependencyIn(
  document: Document,
  path: string[],
  { newName, newValue }: { newName?: string; newValue?: string },
): boolean {
  const parentPath = path.slice(0, -1)
  const relevantItemKey = path.at(-1)

  const parentNode = document.getIn(parentPath)

  if (!parentNode || !isCollection(parentNode)) {
    return false
  }

  const relevantNode = parentNode.items.find(
    item => isPair(item) && isScalar(item.key) && item.key.value === relevantItemKey,
  )

  if (!relevantNode || !isPair(relevantNode)) {
    return false
  }

  if (newName) {
    /* the try..catch block above already throws if a key is an alias */
    CST.setScalarValue(relevantNode.srcToken!.key!, newName)
  }

  if (newValue) {
    // We only support scalar values when substituting. This explicitly avoids
    // substituting aliases, since those can be resolved from a shared location,
    // and replacing either the referrent anchor or the alias would be wrong in
    // the general case. We leave this up to the user, e.g. via a Regex custom
    // manager.
    if (!CST.isScalar(relevantNode.srcToken?.value)) {
      return false
    }
    CST.setScalarValue(relevantNode.srcToken.value, newValue)
  }

  return true
}

/**
 * Updates a dependency version in a PNPM/Yarn `catalog` or `catalogs` section.
 *
 * The function parses the YAML, validates it against `CatalogsConfig`, and
 * applies the change through CST tokens to preserve original formatting (such
 * as quotes, spacing, and comments) as much as possible.
 *
 * Returns the updated YAML string when the change succeeds. Returns the
 * original `fileContent` when the target dependency already has `newValue`.
 * Returns `null` when schema validation fails or when the target key/value
 * cannot be safely updated (for example, alias-based values). Throws on YAML
 * syntax errors and, when `options` is provided, reports them via
 * `programError`.
 */
export function updateYamlCatalogDependencies({
  fileContent,
  upgrade,
  options,
  filePath,
}: UpdateYamlCatalogDependenciesArgs): string | null {
  const { path } = upgrade

  if (!(path.length > 1) && path[0] !== 'catalog' && path[0] !== 'catalogs') {
    return null
  }

  const { newValue } = upgrade

  let tokens: CST.Token[]
  let document: Document | undefined
  let parsedContents: CatalogsConfig

  try {
    // Parse to CST tokens and compose the AST from those same tokens (keepSourceTokens), so the AST
    // nodes reference the original CST tokens. We manipulate via the AST for convenience, then
    // stringify the whole token stream. Stringifying document.contents alone would drop everything
    // outside the top-level map (leading blank lines, directives, pre-document comments).
    tokens = [...new Parser().parse(fileContent)]
    document = [...new Composer({ keepSourceTokens: true }).compose(tokens)][0]
  } catch (err) {
    throwYamlSyntaxError(err, { options, filePath })
  }

  if (!document) {
    throwYamlSyntaxError(new Error('No YAML document found.'), { options, filePath })
  }

  if (document.errors.length > 0) {
    throwYamlSyntaxError(document.errors[0], { options, filePath })
  }

  try {
    parsedContents = parseCatalogsConfig(document.toJSON())
  } catch {
    return null
  }

  const nestedWorkspaces =
    parsedContents.workspaces && !Array.isArray(parsedContents.workspaces) ? parsedContents.workspaces : undefined

  const oldVersion =
    path[0] === 'catalog'
      ? parsedContents.catalog?.[path[1]]
      : path[0] === 'catalogs'
        ? parsedContents.catalogs?.[path[1]]?.[path[2]]
        : path[0] === 'workspaces' && path[1] === 'catalog'
          ? nestedWorkspaces?.catalog?.[path[2]]
          : path[0] === 'workspaces' && path[1] === 'catalogs'
            ? nestedWorkspaces?.catalogs?.[path[2]]?.[path[3]]
            : undefined

  if (oldVersion === newValue) {
    return fileContent
  }

  const didModify = changeDependencyIn(document, path, {
    newValue,
    newName: upgrade.path.at(-1),
  })

  if (!didModify) {
    // Case where we are explicitly unable to substitute the key/value, for
    // example if the value was an alias.
    return null
  }

  return tokens.map(token => CST.stringify(token)).join('')
}
